"use client";

import { Calendar, Loader, Mail, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Textarea } from "@/components/ui/textarea";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const companySizeOptions = [{
  value: "1-50",
  label: "1–50"
}, {
  value: "51-500",
  label: "51–500"
}, {
  value: "501-5000",
  label: "501–5000"
}, {
  value: "5001+",
  label: "5001+"
}];
const contactRows = [{
  icon: Mail,
  label: "Email\xA0",
  value: "contact@yasindhuravith.com\xA0"
}, {
  icon: Phone,
  label: "Phone",
  value: "+94 740 688 693"
}, {
  icon: Calendar,
  label: "Location",
  value: "Maharagama, Sri Lanka"
}];
function TransformationForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="p-8 rounded-lg border border-border bg-card/50">
        <p className="text-foreground font-default text-lg font-semibold mb-2">Message Sent.</p>
        <p className="text-muted-foreground text-sm">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="mt-6 border-border text-foreground hover:bg-secondary">
          Send another message
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="name" rules={{
        required: "Name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-muted-foreground text-xs uppercase tracking-wider font-default">
                Your Name
              </FormLabel>
              <FormControl>
                <Input placeholder="John Doe" className="bg-transparent border-0 border-b border-border px-0 py-2 text-foreground placeholder:text-muted-foreground/60 focus-visible:border-primary focus-visible:ring-0" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Invalid email"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-muted-foreground text-xs uppercase tracking-wider font-default">
                Email Address
              </FormLabel>
              <FormControl>
                <Input type="email" placeholder="john@example.com" className="bg-transparent border-0 border-b border-border px-0 py-2 text-foreground placeholder:text-muted-foreground/60 focus-visible:border-primary focus-visible:ring-0" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="company" rules={{
        required: "Company is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-muted-foreground text-xs uppercase tracking-wider font-default">
                Company Name
              </FormLabel>
              <FormControl>
                <Input placeholder="Your Brand" className="bg-transparent border-0 border-b border-border px-0 py-2 text-foreground placeholder:text-muted-foreground/60 focus-visible:border-primary focus-visible:ring-0" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
        <FormField control={control} name="phone" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-muted-foreground text-xs uppercase tracking-wider font-default">
                Phone Number
              </FormLabel>
              <FormControl>
                <Input type="tel" placeholder="+94 70 000 0000" className="bg-transparent border-0 border-b border-border px-0 py-2 text-foreground placeholder:text-muted-foreground/60 focus-visible:border-primary focus-visible:ring-0" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="company_size" render={({
      field
    }) => <FormItem>
            <FormLabel className="text-muted-foreground text-xs uppercase tracking-wider font-default">
              Project Scale
            </FormLabel>
            <Select onValueChange={field.onChange} value={field.value || ""}>
              <FormControl>
                <SelectTrigger className="w-full bg-transparent border-0 border-b border-border px-0 py-2 text-foreground focus-visible:border-primary focus-visible:ring-0 h-auto">
                  <SelectValue placeholder="Select project scale" />
                </SelectTrigger>
              </FormControl>
              <SelectContent className="bg-popover border-border">
                {companySizeOptions.map((opt, i) => <SelectItem key={opt.value} value={opt.value} data-index={i}>
                    {opt.label}
                  </SelectItem>)}
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="message" render={({
      field
    }) => <FormItem>
            <FormLabel className="text-muted-foreground text-xs uppercase tracking-wider font-default">
              How can I help?
            </FormLabel>
            <FormControl>
              <Textarea rows={5} placeholder="Tell me about your project goals and vision." className="bg-transparent border-0 border-b border-border px-0 py-2 text-foreground placeholder:text-muted-foreground/60 focus-visible:border-primary focus-visible:ring-0 resize-none" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 border border-destructive/40 rounded-md bg-destructive/10">
          <p className="text-destructive text-sm">{submitError}</p>
        </div>}

      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 pt-2">
        <p className="text-xs text-muted-foreground font-default order-2 md:order-1">
          I typically respond within 24 hours.
        </p>
        <Button type="submit" disabled={isSubmitting} className="w-full md:w-auto bg-gradient-to-r from-purple-600 to-blue-600 text-white hover:opacity-90 rounded-lg h-12 px-8 font-semibold order-1 md:order-2">
          {isSubmitting ? <>
              <Loader className="animate-spin mr-2 size-4" />
              Sending...
            </> : "Send Message"}
        </Button>
      </div>
    </Form>;
}
export default function Contact() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    once: true,
    threshold: 0.15
  });
  return <section data-section-id="12343" id="start-a-project" className="bg-background text-foreground py-12 md:py-16 lg:py-20 px-5 md:px-10">
      <div className="max-w-[1280px] mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-10 items-start">
          {}
          <div className="lg:col-span-5">
            <h1 className="font-default text-foreground text-4xl md:text-5xl lg:text-6xl font-bold leading-[1.1] tracking-tighter">
              Let's build{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-500 to-blue-500">something great</span>
              {"."}
            </h1>

            <p className="mt-6 text-muted-foreground text-base md:text-lg leading-relaxed max-w-[380px]">
              Ready to scale your brand? Reach out to discuss your digital marketing needs, content strategy, or web development projects.
            </p>

            <div className="mt-10 space-y-4">
              {contactRows.map((row, i) => {
              const Icon = row.icon;
              return <div key={i} data-index={i} className="flex items-center gap-3">
                    <Icon className="size-5 text-primary shrink-0" />
                    <span className="text-foreground font-default text-[0.9375rem] font-medium">
                      {row.label}
                    </span>
                    <span className="font-default text-sm text-muted-foreground">
                      {row.value}
                    </span>
                  </div>;
            })}
            </div>

            <Separator className="my-8 bg-border" />

            <p className="text-sm text-muted-foreground leading-relaxed max-w-[380px]">
              I am currently accepting new freelance projects and long-term collaborations. Let's connect.
            </p>
          </div>

          {}
          <div className="lg:col-span-7">
            <div ref={ref} className={["rounded-2xl border border-border p-7 md:p-10", "bg-card", "shadow-lg", "transition-all duration-[520ms] ease-out", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"].join(" ")}>
              <FormProvider formKey="start_project_form_portfolio" formId="start_project_form_portfolio" sectionName="start-a-project" defaultValues={{
              name: "",
              email: "",
              company: "",
              phone: "",
              company_size: "",
              message: ""
            }} validationRules={{
              name: {
                required: true
              },
              email: {
                required: true,
                pattern: "email"
              },
              company: {
                required: true
              }
            }} submitText="Send Message" successMessage="Thanks for reaching out! I'll get back to you as soon as possible." errorMessage="Something went wrong. Please try again later.">
                <TransformationForm />
              </FormProvider>
            </div>
          </div>
        </div>
      </div>
    </section>;
}